import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, SlidersHorizontal, ShieldCheck } from 'lucide-react';
import Layout from '../components/layout/Layout';
import ProductCard from '../components/product/ProductCard';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import SEO from '../components/common/SEO';
import AgeGateModal from './AgeGateModal';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const CategoryPage = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const { categories, products, getProductsByCategory, user, isAuthenticated } = useApp();
  const [sortBy, setSortBy] = useState('newest');
  const [showAgeGate, setShowAgeGate] = useState(false);
  const [ageVerified, setAgeVerified] = useState(user?.age_verified || false);
  const [adultProducts, setAdultProducts] = useState([]);

  // Handle both string and integer category IDs
  const category = categories.find(c => c.id === categoryId || c.id === parseInt(categoryId) || String(c.id) === categoryId);
  
  const isAdultCategory = category?.is_adult || category?.name === '18+ Adults Only';

  // 18+ now lives in The Private Collection
  useEffect(() => {
    if (isAdultCategory) navigate('/private-collection', { replace: true });
  }, [isAdultCategory, navigate]);
  
  useEffect(() => {
    if (user?.age_verified) setAgeVerified(true);
  }, [user]);

  // Age gate for 18+ category
  useEffect(() => {
    if (isAdultCategory) {
      if (!isAuthenticated) {
        navigate('/categories');
        return;
      }
      if (!ageVerified && !user?.age_verified) {
        setShowAgeGate(true);
      } else {
        fetchAdultProducts();
      }
    }
  }, [isAdultCategory, ageVerified]);

  const fetchAdultProducts = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/products?category=18%2B+Adults+Only&include_adult=true`);
      const data = await res.json();
      setAdultProducts(Array.isArray(data) ? data : []);
    } catch {
      setAdultProducts([]);
    }
  };

  if (!category) {
    return (
      <Layout title="Category" showBack>
        <div className="flex flex-col items-center justify-center py-20">
          <p className="text-gray-500">Category not found</p>
          <Button onClick={() => navigate('/categories')} variant="link" className="text-minted-600">
            View all categories
          </Button>
        </div>
      </Layout>
    );
  }

  const categoryProducts = isAdultCategory ? adultProducts : getProductsByCategory(category.name);

  // Sort products
  const sortedProducts = [...categoryProducts].sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return a.price - b.price;
      case 'price-high':
        return b.price - a.price;
      case 'newest':
      default:
        return new Date(b.createdAt || b.created_at) - new Date(a.createdAt || a.created_at);
    }
  });

  // If adult category and not verified, show blocked state
  if (isAdultCategory && !ageVerified && !user?.age_verified) {
    return (
      <Layout showBack showSearch>
        <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
          <ShieldCheck className="w-12 h-12 text-minted-600 mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Age Verification Required</h2>
          <p className="text-sm text-gray-500 mb-6">You must verify you are 18 or older to view this section.</p>
          <Button className="bg-minted-600 hover:bg-minted-700" onClick={() => setShowAgeGate(true)}>
            Verify My Age
          </Button>
        </div>
        <AgeGateModal 
          open={showAgeGate} 
          onOpenChange={(open) => {
            setShowAgeGate(open);
            if (!open && !ageVerified) navigate('/categories');
          }}
          onVerified={() => { setAgeVerified(true); fetchAdultProducts(); }}
        />
      </Layout>
    );
  }

  return (
    <Layout showBack showSearch>
      {/* SEO Meta Tags */}
      <SEO
        title={`${category.name} for Sale`}
        description={`Buy and sell ${category.name.toLowerCase()}. Browse ${sortedProducts.length} items on Minted marketplace.`}
        url={`/category/${categoryId}`}
      />
      
      {/* Category Header */}
      <div className="relative h-32 overflow-hidden">
        <img
          src={category.image}
          alt={category.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/20" />
        <div className="absolute bottom-4 left-4">
          <span className="text-3xl mb-1 block">{category.icon}</span>
          <h1 className="text-white font-bold text-xl">{category.name}</h1>
          <p className="text-white/80 text-sm">{sortedProducts.length} items</p>
        </div>
      </div>

      {/* Sort & Filter */}
      <div className="bg-white px-4 py-3 border-b flex items-center justify-between">
        <p className="text-sm text-gray-600">{sortedProducts.length} results</p>
        <div className="flex items-center gap-2">
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[140px] h-9">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="price-low">Price: Low</SelectItem>
              <SelectItem value="price-high">Price: High</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Products Grid */}
      <div className="p-4">
        {sortedProducts.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-gray-500 mb-2">No items in this category yet</p>
            <Button onClick={() => navigate('/sell')} className="bg-minted-600 hover:bg-minted-700">
              Be the first to list
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {sortedProducts.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
      
      {/* Age Gate Modal */}
      {isAdultCategory && (
        <AgeGateModal 
          open={showAgeGate} 
          onOpenChange={(open) => {
            setShowAgeGate(open);
            if (!open && !ageVerified) navigate('/categories');
          }}
          onVerified={() => { setAgeVerified(true); fetchAdultProducts(); }}
        />
      )}
    </Layout>
  );
};

export default CategoryPage;